import React from 'react';
import {Map} from 'immutable';

import {Plugins, Atom} from '@packages/systems/wfdl';
import {iconName, qualify, typeName} from '@packages/systems/wfdl-Qualified';

import {TTypeConstructor, TTypeApplication} from '@packages/systems/wfdl-ast';
import {reportBug} from '@packages/utilities/reporter';
import {useWidgetConfig} from '@packages/shared/helpers/WidgetHelper';
import {extraAtomFields} from '@packages/shared/constants/Atoms';
import {usePreviewMode, useRuntimeEnvironment} from '@packages/systems/render';
import {
  MENU_STATE_HIDE,
  MENU_STATE_SHOW,
} from '@packages/utilities/expression-path-constants';
import {
  type EvaluatedNodeData,
  getChildren,
  getInPojo,
  getStringOrElse,
  getTag,
} from '@packages/utilities/data-utils';

const getDataNavbar = (k: string, data: EvaluatedNodeData) =>
  getInPojo(data, ['navbar', k]);
const getDataAttr = (k: string, data: EvaluatedNodeData) =>
  getInPojo(data, ['attr', k]);

// We use `data.navbar` when it's available, but if it's not, we fallback onto
// `data.attr`. This whole block is temporary until we ship a migration /
// patch to move everything off of `data.navbar` and onto `data.attr`. We have
// to do this in phases so that if we were to revert the migration the code
// still works. After we know the migration is successful (24 hours), we can
// remove this block and use `attributes` directly.
const getDataOrAttr = (
  dataName: string,
  attrName: string,
  defaultValue: string,
  elementData: EvaluatedNodeData
) => {
  const data = getDataNavbar(dataName, elementData);
  if (data !== undefined) return data;
  const attr = getDataAttr(attrName, elementData);
  if (attr !== undefined) return attr;

  reportBug(
    'PlatformNavbarWrapperDataOrAttrError',
    'NavbarWrapper has an undefined property on either data.navbar or data.attr.',
    {
      data: elementData,
      dataName,
      team: 'platform',
      ticket: 'https://webflow.atlassian.net/browse/PLAT-1012',
    }
  );

  return defaultValue;
};

const className = 'w-nav';

const NavbarWrapper = Atom.createRender(
  ({attributes, classes, generateAttributes, context, renderChild, data}) => {
    const previewMode = usePreviewMode();
    const runtimeEnvironment = useRuntimeEnvironment();

    const menuValue = getStringOrElse(
      getInPojo(data, ['temp', 'navbar', 'menu']),
      MENU_STATE_HIDE
    );
    const isOpen = menuValue === MENU_STATE_SHOW;

    const nativeNodeRef = useWidgetConfig(
      className,
      runtimeEnvironment,
      previewMode,
      {open: isOpen}
    );

    const Tag = getTag(getInPojo(data, ['tag']))!;
    const navbarWrapperClasses = classes.push(className);

    const animation = getDataOrAttr(
      'animation',
      'data-animation',
      'default',
      data
    );
    const collapse = getDataOrAttr('collapse', 'data-collapse', 'medium', data);
    const duration = getDataOrAttr('duration', 'data-duration', '400', data);
    const easing = getDataOrAttr('easing', 'data-easing', 'ease', data);
    const easing2 = getDataOrAttr('easing2', 'data-easing2', 'ease', data);
    let docHeight = getDataOrAttr(
      'docHeight',
      'data-doc-height',
      'false',
      data
    );
    let noScroll = getDataOrAttr('noScroll', 'data-no-scroll', 'false', data);

    const dataAttributes = Map({
      'data-animation': animation,
      'data-collapse': collapse,
      'data-duration': duration,
      'data-easing': easing,
      'data-easing2': easing2,
      'data-doc-height': docHeight,
      'data-no-scroll': noScroll,
    });

    // @ts-expect-error - TS2769 - No overload matches this call.
    let newAttributes = attributes.merge(dataAttributes);

    // Both `data-doc-height` and `data-no-scroll` are Boolean types but require
    // special handling to render true as "1" or be deleted if false.
    docHeight = newAttributes.get('data-doc-height');
    noScroll = newAttributes.get('data-no-scroll');

    newAttributes =
      docHeight === true || docHeight === '1'
        ? newAttributes.set('data-doc-height', '1')
        : newAttributes.delete('data-doc-height');
    newAttributes =
      noScroll === true || noScroll === '1'
        ? newAttributes.set('data-no-scroll', '1')
        : newAttributes.delete('data-no-scroll');

    newAttributes = newAttributes.set('role', 'banner');

    const updatedContext = context.set('isOpen', isOpen);

    return (
      <Tag
        ref={nativeNodeRef}
        {...generateAttributes(newAttributes, navbarWrapperClasses)}
      >
        {getChildren(data).map((child) => renderChild(child, updatedContext))}
      </Tag>
    );
  }
);

const config = Atom.create({
  displayName: 'Navbar',
  constraints: {
    descendants: [
      {
        is: 'NavbarWrapper',
        rule: 'Forbid',
      },
    ],
  },
  icon: {
    type: 'Component',
    value: qualify(Plugins.name('Navbar'), iconName('Nav')),
  },
  showEmpty: true,
  render: NavbarWrapper,
  dataType: TTypeApplication({
    con: TTypeConstructor(
      qualify(Plugins.name('Navbar'), typeName('NavbarWrapper'))
    ),
    arg: extraAtomFields,
  }),
  showSettingsInDescendants: true,
});

export default config;
